import { useCustomer } from "@lib/context/shared/customer-context";
import { useErrorPage } from "@lib/context/shared/errors-context";
import { useModalMessages } from "@lib/context/shared/modal-message-context";
import ERROR_CODES from "@lib/error-codes";
import useMuscleRouter from "./use-muscle-router";
import { useTranslations } from "next-intl";
import APP_CONFIGURATION from "@lib/app.config";
import mixpanelService from "@lib/mixpanel/mixpanel.service";
import { MIXPANEL_EVENTS } from "@lib/mixpanel/mixpanel.event-list";
import errorImagesStoryblokService from "@lib/storyblok/services/financial-entity-services/error-images-service";
import { cn } from "@lib/cn";
import { useMediaQuery } from "react-responsive";
import { BREAKPOINTS } from "@lib/constants";
const useErrorsManagement = () => {
  const t = useTranslations();
  const router = useMuscleRouter();
  const isMobile = useMediaQuery({
    query: "(max-width: ".concat(BREAKPOINTS.md, "px)")
  });
  const {
    logoutFromFrontend
  } = useCustomer();
  const {
    renderErrorPage
  } = useErrorPage();
  const {
    showModalMessage,
    setModalIsOpen
  } = useModalMessages();
  const actionsOnlyGoHome = [{
    buttonVariant: isMobile ? "outlineXL" : "outlineMd",
    buttonColor: APP_CONFIGURATION.colors.secondary_900,
    text: t("Shared.Errors.RETURN_TO_HOME"),
    onClick: () => router.goHome()
  }];
  const goHomeAndContinue = [{
    buttonVariant: isMobile ? "solidXL" : "solidMd",
    buttonColor: APP_CONFIGURATION.colors.secondary_900,
    text: t("Shared.Errors.RETURN_TO_HOME"),
    onClick: () => router.goHome()
  }, {
    buttonVariant: isMobile ? "outlineXL" : "outlineMd",
    buttonColor: APP_CONFIGURATION.colors.secondary_900,
    text: t("Shared.CONTINUE"),
    onClick: () => setModalIsOpen(false)
  }];
  const manageOnlyAuthorizedError = errorCode => {
    switch (errorCode) {
      case ERROR_CODES.Shared.UNAUTHORIZED_ERROR:
      case ERROR_CODES.Third.UNAUTHORIZED_ERROR:
        showUnauthorizedErrorModal();
        break;
    }
  };
  const manageSharedError = async function (errorCode) {
    let manageUnknownErrorWith500Screen = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    switch (errorCode) {
      case ERROR_CODES.Shared.UNAUTHORIZED_ERROR:
      case ERROR_CODES.Third.UNAUTHORIZED_ERROR:
        showUnauthorizedErrorModal();
        break;
      case ERROR_CODES.Shared.REQUEST_CANCELED:
        break;
      //This behavior is intended to make the second request proceed and not be blocked
      default:
        mixpanelService.trackEvent(MIXPANEL_EVENTS.Global.UNKNOWN_INTERRUPTION_MODAL, {
          error_code: errorCode
        });
        manageUnknownErrorWith500Screen ? renderErrorPage({
          code: "500"
        }) : showUnknownErrorModal();
        break;
    }
  };
  const manageBackendTranslatedError = function (error, actions) {
    let closeable = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    let image = arguments.length > 3 ? arguments[3] : undefined;
    switch (error.code) {
      case ERROR_CODES.Shared.UNAUTHORIZED_ERROR:
      case ERROR_CODES.Third.UNAUTHORIZED_ERROR:
        showUnauthorizedErrorModal();
        return;
      // Use unknow modal on network errors
      case ERROR_CODES.Shared.UNKNOWN_ERROR:
      case ERROR_CODES.Shared.CONNECTION_REFUSED:
      case ERROR_CODES.Shared.NETWORK_ERROR:
        mixpanelService.trackEvent(MIXPANEL_EVENTS.Global.UNKNOWN_INTERRUPTION_MODAL, {
          error: error
        });
        showUnknownErrorModal();
        return;
      case ERROR_CODES.Flights.ERROR_ON_PROCESSING_FLOW:
      case ERROR_CODES.Flights.ERROR_FLOW_001:
      case ERROR_CODES.Flights.ERROR_FLOW_003:
      case ERROR_CODES.Flights.ERROR_FLOW_004:
        mixpanelService.trackEvent(MIXPANEL_EVENTS.Global.GENERIC_ERROR_ON_PROCESSING_FLOW, {
          error: error,
          code: error.code,
          ui_replaced: true
        });
        showGenericErrorOnProcessingFlowModal('generic');
        return;
      case ERROR_CODES.Flights.ERROR_FLOW_002:
        mixpanelService.trackEvent(MIXPANEL_EVENTS.Global.GENERIC_ERROR_ON_PROCESSING_FLOW, {
          error: error,
          code: error.code,
          ui_replaced: true
        });
        showGenericErrorOnProcessingFlowModal('churning');
        return;
    }
    showModalMessage({
      closeable: closeable,
      title: error.error,
      message: error.message,
      image: image,
      actions: actions
    });
  };
  const showUnknownErrorModal = async () => {
    showModalMessage({
      closeable: false,
      onClose: () => router.goHome(),
      title: t("Shared.Errors.UNKNOWN_ERROR"),
      message: t("Shared.Errors.UNKNOWN_ERROR_MESSAGE"),
      actions: [{
        text: t("Shared.Errors.RETURN_TO_HOME"),
        onClick: () => router.goHome(),
        buttonVariant: isMobile ? "solidXL" : "solidLg"
      }]
    });
  };
  const showGenericErrorOnProcessingFlowModal = async errror_type => {
    const image = await errorImagesStoryblokService.getErrorImage('generic' === errror_type ? "flow_error" : "max_retries");
    showModalMessage({
      closeable: false,
      title: errror_type === 'generic' ? t("Shared.Errors.ERROR_ON_PROCESSING_FLOW") : t("Shared.Errors.ERROR_FLOW_002"),
      message: errror_type === 'generic' ? t("Shared.Errors.ERROR_ON_PROCESSING_FLOW_MESSAGE") : t("Shared.Errors.ERROR_FLOW_002_MESSAGE"),
      image: image,
      richMessage: {
        text: errror_type === 'generic' ? t("Shared.Errors.ERROR_ON_PROCESSING_FLOW_MESSAGE", {
          phoneNumber: APP_CONFIGURATION.config.contactInformation.bankPhoneNumber
        }) : t("Shared.Errors.ERROR_FLOW_002_MESSAGE", {
          phoneNumber: APP_CONFIGURATION.config.contactInformation.bankPhoneNumber
        }),
        textStyle: cn(APP_CONFIGURATION.colors.globals.modal.modal_description_class, APP_CONFIGURATION.config.rich_message_config.text_style),
        linksStyle: APP_CONFIGURATION.config.rich_message_config.links_style,
        boldStyle: APP_CONFIGURATION.config.rich_message_config.bold_style,
        links: ['tel:' + APP_CONFIGURATION.config.contactInformation.bankPhoneNumber]
      },
      actions: [{
        text: t("Shared.ACCEPT"),
        onClick: () => router.goHome()
      }]
    });
  };
  const showUnauthorizedErrorModal = () => {
    showModalMessage({
      closeable: false,
      title: t("Shared.Errors.UNAUTHORIZED_ERROR"),
      message: t("Shared.Errors.UNAUTHORIZED_ERROR_MESSAGE"),
      actions: [{
        text: t("Shared.Errors.RETURN_TO_HOME"),
        onClick: () => {
          logoutFromFrontend();
        },
        buttonVariant: isMobile ? "solidXL" : "solidLg"
      }]
    });
  };
  return {
    manageSharedError,
    manageBackendTranslatedError,
    manageOnlyAuthorizedError,
    showUnauthorizedErrorModal,
    actionsOnlyGoHome,
    goHomeAndContinue
  };
};
export default useErrorsManagement;